<template>
  <SnowLocationPageContent
    :can-show-select-favorite-list-dialog="true"
    :location="location"
    selected-tab-title="Weather Stations"
  >
    <NoFavoritesMessage
      v-if="!hasWeatherStations"
      class="tw-py-10 lg:tw-py-16"
      :is-favorites="false"
      view="weather-stations"
    />
    <div v-else>
      <div class="tw-flex tw-justify-flex-start tw-flex-wrap tw-mb-2 lg:tw-mb-4 tw-px-2 tw-py-4">
        <h2 class="section-title heading-color">
          Nearby Weather Stations
        </h2>
        <EstimatedBadge
          v-if="showBadge"
          additional-class-names="tw-ml-2"
          padding-y="tw-py-0.5"
        >
          Experimental
        </EstimatedBadge>
        <p class="tw-w-full tw-mt-0.5 tw-text-xs lg:tw-text-sm text-regular-color">
          The latest snowfall data from nearby weather stations, usually updated hourly.
        </p>
      </div>
      <LocationLinkButton
        v-if="hasWeatherStations && weatherStations.length > 1"
        additional-class-names="tw-w-full tw-mb-4 lg:tw-mb-8"
        :to="viewAllWeatherStationsOnMapUrl"
      >
        <font-awesome-icon
          class="tw-fill-current tw-mr-2"
          :icon="['fad', 'map-location-dot']"
        />
        View All Stations on Map
      </LocationLinkButton>
      <WeatherStationCard
        v-for="(weatherStation) in weatherStations"
        :key="weatherStation.id"
        :can-show-recent-snow-icon="false"
        card-class-names="tw-mb-4 lg:tw-mb-8"
        :location="location"
        :range="range"
        :show-weather-station-dialog="true"
        :weather-station="weatherStation"
      />
    </div>
    <WeatherStationDialog
      :in-location-page="true"
    />
  </SnowLocationPageContent>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { points } from '@turf/helpers';
import { addQueryParamsToPath, parseOpenMountainApiError } from '@@/utils/CommonUtils';
import { useLocationStore } from '@@/stores/Location';
import bbox from '@turf/bbox';
import LocationPageMixin, { getSetupParams } from '@@/components/Location/Mixins/LocationPageMixin';
import LocationWeatherStationMixin, { getObservationHourlyRange } from '@@/components/Location/Mixins/LocationWeatherStationMixin';

export default {
  name: 'LocationWeatherStationsPage',

  mixins: [LocationPageMixin, LocationWeatherStationMixin],

  async setup() {
    const locationStore = useLocationStore();
    const params = getSetupParams();

    const { data, error } = await useAsyncData(() => locationStore.fetchWeatherStations(params));

    if (error.value) {
      throw createError(parseOpenMountainApiError(error.value));
    }

    const location = ref(data.value);
    const range = ref(getObservationHourlyRange(location.value?.weather_stations));

    return {
      location,
      range,
    };
  },

  computed: {
    ...mapState(useMetaStore, ['dataSourcesById']),

    showBadge() {
      const fields = ['observation_hourly'];
      return isExperimentalSource(this.sourceId, this.dataSourcesById, fields);
    },

    sourceId() {
      if (this.hasWeatherStations) {
        return this.weatherStations[0].source_id;
      }

      return null;
    },

    viewAllWeatherStationsOnMapUrl() {
      if (this.hasWeatherStations) {
        const weatherStationPoints = this.weatherStations.map((weatherStation) => weatherStation.coordinates.point);
        const bounds = bbox(points([...weatherStationPoints]));
        const url = addQueryParamsToPath('/map/none/weather-stations', { bounds, canSaveMapState: false });
        return url;
      }

      return '';
    },
  },

  methods: {
    ...mapActions(useLocationStore, ['fetchWeatherStations']),

    async fetchLocation(params = {}) {
      const location = await this.fetchWeatherStations(params);
      this.range = getObservationHourlyRange(location.weather_stations);
      return location;
    },
  },
};
</script>
